import type { FeatureCollection } from "@turf/helpers";
import type { Map as MapType } from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Map as MapComponent, MapPlaceholder } from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { diffObjects } from "senf-shared/utils/helpers";
import { cn } from "../../../util/classNameConstructor";
import type { GISData } from "./Map.types";
import { flyToPolygon } from "./utils/flyToPolygon";

type MapSettings = {
  center: [number, number];
  pitch: number;
  bearing: number;
  zoom?: number;
  height?: number;
};

type MapViewEditorProps = {
  children: React.ReactNode;
  mapSettings: MapSettings;
  shouldFlyTo: MapSettings;
  setMapSettings: (mapSettings: MapSettings | null) => void;
  accessibilityInfotext: string;
  allowFullscreen: boolean;
  mapData?: {
    mapbounds: FeatureCollection;
    mapStyle: string;
    gisData: GISData;
  };
  resizeDependencies?: string[];
  flyToDependencies?: string[];
  mode: string;
  statefulMap: MapType;
  setStatefulMap: React.Dispatch<React.SetStateAction<MapType>>;
  setFullscreenMap: (fullscreenMap: boolean) => void;
  fullscreenMap: boolean;
  setShowMapDataSetList: React.Dispatch<React.SetStateAction<boolean>>;
};

const getMapboxView = (map?: MapType): MapSettings | null => {
  if (!map) return null;
  const bearing = map.getBearing();
  return {
    pitch: map.getPitch(),
    // biome-ignore lint/suspicious/noCompareNegZero: getBearing returns -0 for some reason
    bearing: bearing === -0 ? 0 : bearing,
    center: [map.getCenter().lng, map.getCenter().lat],
    zoom: map.getZoom(),
  } satisfies MapSettings;
};

const ViewControls = ({
  mapSettings,
  setMapSettings,
  mapData,
  statefulMap,
  tempMapView,
  setTempMapView,
  flyTo,
}: {
  mapSettings: MapSettings;
  setMapSettings: (mapSettings: MapSettings | null) => void;
  mapData: MapViewEditorProps["mapData"];
  statefulMap: MapType;
  tempMapView: MapSettings | null;
  setTempMapView: React.Dispatch<React.SetStateAction<MapSettings | null>>;
  flyTo: (duration?: number) => void;
}) => {
  const { t } = useTranslation();

  const showResetViewButton = useMemo(() => {
    return !!mapSettings;
  }, [mapSettings]);

  const isCesiumMap =
    mapData?.mapStyle === "cesium" || mapData?.mapStyle === "cesium-rendering";

  const getCurrentMapView = useCallback(
    (map?: MapType) => {
      if (isCesiumMap) {
        return tempMapView;
      }
      return getMapboxView(map || statefulMap);
    },
    [isCesiumMap, tempMapView, statefulMap],
  );
  const [initialMapView, setInitialMapView] = useState<MapSettings | null>(
    null,
  );
  const showReturnToInitialViewButton = useMemo(() => {
    return (
      initialMapView && tempMapView && diffObjects(tempMapView, initialMapView)
    );
  }, [initialMapView, tempMapView]);
  const showSetViewButton = useMemo(() => {
    return tempMapView && diffObjects(tempMapView || {}, mapSettings || {});
  }, [tempMapView, mapSettings]);

  const handleSetView = () => {
    setMapSettings(getCurrentMapView());
  };

  const handleReset = () => {
    setTempMapView(null);
    setMapSettings(null);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!initialMapView) {
      setInitialMapView(getCurrentMapView());
    }
  }, [statefulMap]);
  return (
    <>
      <div className="z-5 absolute top-0 inset-inline-0 p-3 pointer-events-none flex justify-end gap-2 w-full flex-wrap">
        {(showResetViewButton || showReturnToInitialViewButton) && (
          <Button
            variant="white"
            onClick={() => flyTo()}
            classNameWrapper="flex items-center justify-center me-auto"
            className={cn(
              "shadow-xl",
              showResetViewButton && "text-action-primary-pressed",
              showSetViewButton
                ? "group/reset-view"
                : "cursor-default pointer-events-none",
            )}
            leadingIcon={
              showResetViewButton && !showSetViewButton
                ? "lock-view"
                : "reset-view"
            }
            tooltipLabel={
              showSetViewButton
                ? showResetViewButton
                  ? t("map:builder.returnToView")
                  : t("util:reset", { val: t("global:view") })
                : t("map:builder.viewSet")
            }
            tooltipProps={
              showResetViewButton && !showSetViewButton
                ? {
                    onClick: "openTooltip",
                  }
                : undefined
            }
          />
        )}
        {showSetViewButton && (
          <Button
            leadingIcon="lock-view"
            text={t("map:builder.setView")}
            variant="white"
            className="shadow-xl truncate"
            onClick={handleSetView}
          />
        )}
        {showResetViewButton && (
          <Button
            leadingIcon="delete-view"
            text={t("global:reset")}
            variant="white"
            className="shadow-xl"
            onClick={handleReset}
          />
        )}
      </div>
      {showResetViewButton && (
        <div className="ring-border-highlight-default pointer-events-none absolute top-0 h-full w-full rounded-l-md ring-2 ring-inset" />
      )}
    </>
  );
};

const MapViewEditor = ({
  children,
  mapSettings,
  setMapSettings,
  accessibilityInfotext,
  mapData,
  allowFullscreen,
  resizeDependencies,
  flyToDependencies,
  mode,
  statefulMap,
  setStatefulMap,
  setFullscreenMap,
  fullscreenMap,
  setShowMapDataSetList,
}: MapViewEditorProps) => {
  const { t } = useTranslation();

  const [tempMapView, setTempMapView] = useState<MapSettings | null>(null);

  const isCesiumMap =
    mapData?.mapStyle === "cesium" || mapData?.mapStyle === "cesium-rendering";

  const flyTo = useCallback(
    (duration = 1000) => {
      if (isCesiumMap) {
        // @todo: implemet cesium fly to functionality
        return;
      }
      if (statefulMap) {
        if (mapSettings) {
          statefulMap.flyTo({
            ...mapSettings,
            speed: 2000 / duration,
          });
          return;
        }
        if (mapData?.mapbounds) {
          flyToPolygon(statefulMap, mapData.mapbounds, {
            duration,
            pitch: 0,
            bearing: 0,
          });
        }
      }
    },
    [isCesiumMap, statefulMap, mapSettings, mapData?.mapbounds],
  );

  const initialized = useRef(false);
  useEffect(() => {
    // @todo: add cesium on load event
    statefulMap?.on("load", () => {
      initialized.current = true;
    });
  }, [statefulMap]);

  useEffect(() => {
    setTimeout(() => {
      flyTo(initialized.current ? undefined : 0);
    }, 100);
  }, [flyTo, ...(flyToDependencies || [])]);

  useEffect(() => {
    setTimeout(() => flyTo(), 500);
  }, [flyTo, ...(resizeDependencies || [])]);

  // const events = ["zoom", "drag", "click", "rotate", "pitch"];
  // const addEvent = useCallback(
  //   (eventName: string, handler: (e: unknown) => void) =>
  //     statefulMap?.on(eventName, handler),
  //   [statefulMap],
  // );
  // const removeEvent = useCallback(
  //   (eventName: string, handler: (e: unknown) => void) =>
  //     statefulMap?.off(eventName, handler),
  //   [statefulMap],
  // );

  // useEffect(() => {
  //   const handleInteraction = (e) => {
  //     if (e.originalEvent) {
  //       setMapSettingsDefined(false);
  //       for (const event of events) {
  //         removeEvent(event, handleInteraction);
  //       }
  //     }
  //   };

  //   if (mapSettingsDefined && !isCesiumMap) {
  //     for (const event of events) {
  //       addEvent(event, handleInteraction);
  //     }
  //   }

  //   return () => {
  //     for (const event of events) {
  //       removeEvent(event, handleInteraction);
  //     }
  //   };
  // }, [mapSettingsDefined, addEvent, removeEvent, isCesiumMap]);

  return (
    <>
      {mode === "edit" && !mapData ? (
        <>
          <MapPlaceholder
            className="rounded-e-none rounded-s-md"
            onClick={() => setShowMapDataSetList(true)}
          />
        </>
      ) : (
        <div className="flex h-full w-full justify-center">
          {allowFullscreen && (
            <Button
              variant="outline"
              leadingIcon={
                fullscreenMap ? "arrowhead-down-lg" : "arrowhead-up-lg"
              }
              onClick={() => setFullscreenMap(!fullscreenMap)}
              text={
                fullscreenMap
                  ? t("util:show", { val: t("global:question") })
                  : t("util:expand", { val: t("global:map") })
              }
              className="shadow-xl bg-white"
              classNameWrapper="absolute z-50 m-5"
            />
          )}
          <div className="h-full w-full">
            <MapComponent
              id="map-survey"
              key={mode}
              statefulMap={statefulMap}
              setStatefulMap={setStatefulMap}
              optimizeString={true}
              resize={resizeDependencies?.toString()}
              mapbounds={mapData?.mapbounds}
              mapStyle={mapData?.mapStyle}
              gisData={mapData?.gisData}
              accessibilityInfotext={accessibilityInfotext}
              onCameraMove={(e) => {
                setTempMapView(
                  e.originalEvent ? getMapboxView(e?.target) : null,
                );
              }}
              // @todo: maybe consolidate cesium camera move and default camera move
              onCesiumCameraMove={setTempMapView}
              cesiumCameraPosition={tempMapView}
              shouldFlyTo={false}
              flyToMapbounds={false}
            />
            {mode === "edit" && (
              <ViewControls
                mapSettings={mapSettings}
                setMapSettings={setMapSettings}
                mapData={mapData}
                statefulMap={statefulMap}
                tempMapView={tempMapView}
                setTempMapView={setTempMapView}
                flyTo={flyTo}
              />
            )}
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default MapViewEditor;
